import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import logo from '../../assets/images/logo-artie.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PROJECT_TYPES = [
  { value: "Vertical Video", label: "Vertical Video" },
  { value: "Long form Video", label: "Long form Video" },
  { value: "Feature Length Film", label: "Feature Length Film" },
  { value: "Podcast & Audio", label: "Podcast & Audio" },
];

const projectSchema = z.object({
  title: z.string().min(1, "Project title is required").max(200),
  project_type: z.string().min(1, "Project type is required"),
  description: z.string().optional(),
  brand_revenue_target: z.preprocess(
    (val) => (val === "" || val === null || val === undefined ? undefined : Number(val)),
    z.number().min(1, "Budget target is required").positive("Budget must be positive")
  ),
});

type ProjectFormValues = z.infer<typeof projectSchema>;

export default function OnboardingStep4() {
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scriptFile, setScriptFile] = useState<File | null>(null);
  const [isWaitingForScore, setIsWaitingForScore] = useState(false);
  const [pollingMessage, setPollingMessage] = useState("");

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: "",
      project_type: "",
      description: "",
      brand_revenue_target: undefined,
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (PDF, TXT, DOCX, etc.)
      const validTypes = ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.txt')) {
        toast.error("Please upload a PDF, TXT, or DOCX file");
        return;
      }
      setScriptFile(file);
      toast.success("Script file selected");
    }
  };

  const removeFile = () => {
    setScriptFile(null);
    toast.info("Script file removed");
  };

  const pollForScore = async (projectId: string): Promise<boolean> => {
    const maxAttempts = 120; // Poll for up to 2 minutes (120 * 1 second)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        setPollingMessage(`Analyzing your script... (${attempts + 1}s)`);
        
        // Try to get the score
        const score = await apiClient.getArtieScoreByProject(projectId);
        if (score) {
          return true; // Score is ready
        }
      } catch (error: any) {
        // If 404, score doesn't exist yet - continue polling
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          attempts++;
          continue;
        }
        // Other errors - stop polling
        console.error("Error polling for score:", error);
        return false;
      }
    }
    
    return false; // Timeout
  };

  async function onSubmit(data: ProjectFormValues) {
    if (!scriptFile) {
      toast.error("Please upload a script to continue");
      return;
    }

    setIsSubmitting(true);
    try {
      // Create the project
      const projectData: any = {
        title: data.title,
        format: data.project_type, // Map project_type to format field
        logline: data.description || undefined, // Map description to logline
        brand_revenue_target: data.brand_revenue_target || undefined,
      };

      const project: any = await apiClient.createProject(projectData);

      // If there's a script file, upload it after project creation
      if (scriptFile && project?._id) {
        try {
          await apiClient.uploadScript({
            projectId: project._id,
            file: scriptFile,
          });
          
          // Trigger Artie Score calculation
          setIsWaitingForScore(true);
          setPollingMessage("Starting analysis...");
          
          try {
            await apiClient.calculateArtieScore(project._id, false);
          } catch (calcError) {
            console.log("Score calculation triggered, waiting for completion...");
          }
          
          // Poll for score completion
          const scoreReady = await pollForScore(project._id);
          
          if (!scoreReady) {
            toast.warning("Score calculation is taking longer than expected. You can check back later.");
          }
        } catch (error) {
          console.error("Script upload failed:", error);
          toast.warning("Project created but script upload failed. You can upload it later from the project page.");
        }
      }
      
      // Update onboarding status
      await apiClient.updateCurrentUser({
        onboarding_step: 4,
        onboarding_completed: true,
      });

      await checkAuth();

      toast.success("Welcome to Artie! Your project has been created.");
      // Navigate to the project page
      navigate(`/projects/${project._id}`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to complete onboarding");
    } finally {
      setIsSubmitting(false);
      setIsWaitingForScore(false);
      setPollingMessage("");
    }
  }

  const handleSkipProject = async () => {
    setIsSubmitting(true);
    try {
      await apiClient.updateCurrentUser({
        onboarding_step: 4,
        onboarding_completed: true,
      });
      await checkAuth();
      toast.success("Welcome to Artie! You can create a project anytime.");
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      toast.error("Failed to complete onboarding");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center">

      {/* Top Navigation */}
      <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-between z-50 relative">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:border-[oklch(0.2686_0_0)] transition-all"
        >
          <ArrowLeft className="w-5 h-5 stroke-[1.5]" />
        </button>
        <div className="flex items-center gap-4">
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
          </div>
          <span className="text-xs font-medium text-[oklch(0.55_0_0)]">Step 3 of 3</span>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 z-10 flex flex-col w-full max-w-2xl pt-8 pr-6 pb-32 pl-6 items-center">
        {/* Header */}
        <div className="animate-in slide-in-from-bottom-4 duration-500 flex flex-col mb-8 relative items-center">
          <div className="relative w-20 h-20 mb-4">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] rounded-full blur-xl opacity-50"></div>
            <img 
              src={logo} 
              alt="Artie" 
              className="w-full h-full rounded-full object-cover border-4 border-[oklch(1.0000_0_0)] shadow-xl relative z-10 animate-[float_3s_ease-in-out_infinite]" 
            />
            <div className="-right-24 text-[oklch(1.0000_0_0)] transform animate-in slide-in-from-bottom-2 duration-500 delay-300 text-xs font-medium bg-orange-500 z-20 rounded-xl px-3 py-1.5 absolute top-0 shadow-lg rotate-2">
              Almost there! 🎉
            </div>
          </div>
        </div>

        <div className="text-center mb-12 animate-in slide-in-from-bottom-4 duration-500">
          <h1 className="text-3xl md:text-4xl font-semibold font-cabinet tracking-tight text-[oklch(0.2686_0_0)] mb-3">
            Let's create your first project
          </h1>
          <p className="text-[oklch(0.55_0_0)] font-ibm text-sm md:text-base max-w-md mx-auto leading-relaxed">
            Add your project details and script to start your first analysis.
          </p>
        </div>

        {/* Project Creation Section */}
        <div className="w-full animate-in slide-in-from-bottom-4 duration-500 delay-200">
          <Card className="p-6 bg-white border border-[oklch(0.92_0_0)] rounded-2xl shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold font-cabinet text-[oklch(0.2686_0_0)] mb-1">
                Project Details
              </h3>
              <p className="text-sm text-[oklch(0.55_0_0)]">
                Fill in the details below to get started
              </p>
              <div className="mt-3 bg-[oklch(0.97_0.01_60)] p-3 rounded-xl border border-[oklch(0.92_0_0)] text-xs text-[oklch(0.55_0_0)] flex gap-2 items-center">
                <span className="text-lg">🔒</span>
                <span>
                  Your ideas are <strong>safe and private</strong>. Artie does not share your script or project details with third parties without your explicit permission.
                </span>
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">
                        Project Title <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., My Comedy Pilot, Tech Review Series"
                          className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="project_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">
                        Project Type <span className="text-red-500">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-12 rounded-xl border-[oklch(0.92_0_0)]">
                            <SelectValue placeholder="Select project type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PROJECT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="brand_revenue_target"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">
                        How much do you need from brand partners? <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.55_0_0)] font-semibold">
                            $
                          </div>
                          <Input
                            {...field}
                            type="number"
                            placeholder="50,000"
                            className="h-12 pl-8 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">
                        Brief Description (optional)
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Tell us about your project in a sentence or two..."
                          className="rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] min-h-[100px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Script Upload */}
                <div>
                  <label className="block text-sm font-medium text-[oklch(0.2686_0_0)] mb-2">
                    Upload Script <span className="text-red-500">*</span>
                  </label>
                  {scriptFile ? (
                    <div className="flex items-center gap-2 p-3 bg-[oklch(0.97_0.01_60)] rounded-xl border border-[oklch(0.92_0_0)]">
                      <div className="flex-1 flex items-center gap-2">
                        <Upload className="w-4 h-4 text-[oklch(0.70_0.18_55)]" />
                        <span className="text-sm text-[oklch(0.2686_0_0)] truncate">{scriptFile.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-1 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.txt,.doc,.docx"
                        onChange={handleFileSelect}
                      />
                      <div className="border-2 border-dashed border-[oklch(0.92_0_0)] rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-[oklch(0.97_0.01_60)] transition-all">
                        <Upload className="w-8 h-8 text-[oklch(0.55_0_0)] mb-2" />
                        <p className="text-sm font-medium text-[oklch(0.2686_0_0)]">Click to upload script</p>
                        <p className="text-xs text-[oklch(0.55_0_0)] mt-1">PDF, TXT, or DOCX</p>
                      </div>
                    </label>
                  )}
                </div>

                <div className="pt-4 flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSkipProject}
                    disabled={isSubmitting}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Skip Project
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 h-12 rounded-xl bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-white group"
                  >
                    {isWaitingForScore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {pollingMessage}
                      </>
                    ) : isSubmitting ? (
                      "Creating..."
                    ) : (
                      <>
                        Create Project & Finish
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </Card>
        </div>
      </main>
    </div>
  );
}